import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Category } from 'src/generics/data/generic-category/data.types';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { resolveMenuOrder } from 'src/dispensary/hooks/use-dispensary-menu-order';

type UseSetUpRelevanceAsInitialSortParams = {
  allowSortByRelevance: boolean;
  category: Category | null;
};

/**
 * Applies the dispensary's configured default sort ("Relevance") on initial load without
 * disturbing other URL filters -- see ECOMM-12888 and ECOMM-13724. Reads `menuOrderSettings`
 * (v2) or `menuSortSettings` (v1) depending on `ecomm.admin.menu-v2.rollout`.
 */
export function useSetUpRelevanceAsInitialSort({
  allowSortByRelevance,
  category,
}: UseSetUpRelevanceAsInitialSortParams): void {
  const { queryParams, setQueryParams } = useProductQueryParams();
  const { dispensary } = useDispensary();
  const flags = useFlags();
  const isMenuV2Enabled = flags['ecomm.admin.menu-v2.rollout'] ?? false;
  const relevanceSortAdminOptionEnabled = flags['growth.ecomm.relevance-sort-admin-option.rollout'] ?? false;

  // Delegate to resolveMenuOrder (also used by useDispensaryMenuOrder) so category-vs-global
  // resolution order has one source of truth and can't silently diverge.
  const relevanceConfiguredForCategory =
    !!dispensary && resolveMenuOrder(dispensary, category, isMenuV2Enabled) === 'relevance';

  const isRelevanceSort = queryParams.sortby === 'relevance';
  const isDefaultSort = queryParams.sortby === 'default';

  useEffect(() => {
    // Always revert a stale relevance sort once it's no longer allowed (search, logout, kiosk, etc).
    if (!allowSortByRelevance && isRelevanceSort) {
      setQueryParams({ sortby: 'default' });
      return;
    }
    if (!allowSortByRelevance) {
      return;
    }

    // v2, and v1 with the admin option enabled, only apply relevance when configured for category.
    if (isMenuV2Enabled || relevanceSortAdminOptionEnabled) {
      if (relevanceConfiguredForCategory && isDefaultSort) {
        setQueryParams({ sortby: 'relevance' });
      }
      return;
    }

    // Original menu v1 (admin option disabled) behavior: relevance is always the default sort.
    if (isDefaultSort || !queryParams.sortby) {
      setQueryParams({ sortby: 'relevance' });
    }
  }, [
    allowSortByRelevance,
    isRelevanceSort,
    isDefaultSort,
    queryParams.sortby,
    setQueryParams,
    relevanceSortAdminOptionEnabled,
    relevanceConfiguredForCategory,
    isMenuV2Enabled,
  ]);
}
